import { FormattedMessage } from "react-intl";
import SubscriptionPlanFeaturesList from "./SubscriptionPlanFeaturesList";

type Plan = {
  name?: string;
  overview?: string | null;
  price?: string | number | null;
  validity_days?: number | null;
  max_services?: number | null;
  max_staff?: number | null;
  max_offline_booking_staff?: number | null;
  enable_growth_statistics?: number | null;
  enable_appointment_cancellation?: number | null;
};

type Props = {
  plan: Plan;
  startedAt: string | null | undefined;
  expiresAt: string | null | undefined;
  formatDt: (v: string | null | undefined) => string;
};

/**
 * Step 6: full current subscription overview while status is active.
 */
const ActiveSubscriptionOverview = ({
  plan,
  startedAt,
  expiresAt,
  formatDt,
}: Props) => {
  return (
    <div className="card shadow-sm border-success border-opacity-25 mb-4 subscription-active-overview">
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <h2 className="h5 mb-0">
            <FormattedMessage
              id="subscription_current_overview_title"
              defaultMessage="Current subscription"
            />
          </h2>
          <span className="badge bg-success">
            <FormattedMessage id="subscription_status_active" defaultMessage="Active" />
          </span>
        </div>

        <h3 className="h6 text-primary mb-2">{plan.name}</h3>

        {plan.overview ? (
          <p className="text-muted small mb-3">{plan.overview}</p>
        ) : null}

        <dl className="row small mb-3 mb-md-4">
          <dt className="col-sm-4 col-lg-3 mb-1">
            <FormattedMessage id="subscription_price_label" defaultMessage="Price" />
          </dt>
          <dd className="col-sm-8 col-lg-9 mb-2">
            €{Number(plan.price ?? 0).toFixed(2)}
          </dd>

          <dt className="col-sm-4 col-lg-3 mb-1">
            <FormattedMessage
              id="subscription_start_date_label"
              defaultMessage="Start date"
            />
          </dt>
          <dd className="col-sm-8 col-lg-9 mb-2">{formatDt(startedAt)}</dd>

          <dt className="col-sm-4 col-lg-3 mb-1">
            <FormattedMessage
              id="subscription_validity_period_label"
              defaultMessage="Plan validity period"
            />
          </dt>
          <dd className="col-sm-8 col-lg-9 mb-2">
            {plan.validity_days != null ? (
              <FormattedMessage
                id="subscription_validity_days_value"
                defaultMessage="{count} days per billing cycle"
                values={{ count: plan.validity_days }}
              />
            ) : (
              "—"
            )}
          </dd>

          <dt className="col-sm-4 col-lg-3 mb-1">
            <FormattedMessage
              id="subscription_access_until_label"
              defaultMessage="Access until"
            />
          </dt>
          <dd className="col-sm-8 col-lg-9 mb-2">{formatDt(expiresAt)}</dd>
        </dl>

        <SubscriptionPlanFeaturesList plan={plan} />

        <p className="small text-muted mt-3 mb-0">
          <FormattedMessage
            id="subscription_upgrade_hint"
            defaultMessage="To upgrade, choose a higher-priced plan and submit payment proof for admin approval."
          />
        </p>
      </div>
    </div>
  );
};

export default ActiveSubscriptionOverview;
